import { assertNotNull } from "common/assert";
import { load } from "common/loader";
import { OnEvent } from "common/onEvent";
import { cancel, delay } from "common/promises";
import { getSkip } from "common/skipper";
import { announcePaths, paths } from "from-start/scene/resources";
import { html, LitElement } from "lit-element";
import { nothing } from "lit-html";
import { classMap } from "lit-html/directives/class-map";
import { styleMap } from "lit-html/directives/style-map";
import * as R from "ramda";
import { endingEffect } from "./endingEffect";
import * as texts from "./texts";
import { playTicketPrintSound, } from "./ticketPrintSound";
function seq(name, html) {
    return {
        name,
        html,
    };
}
class ChatBotContent extends LitElement {
    constructor() {
        super(...arguments);
        this.sequence = 0;
        this.transition = false;
    }
    static get properties() {
        return {
            sequence: { type: Number },
            progress: { type: Number },
            transition: { type: Boolean },
        };
    }
    createRenderRoot() {
        return this;
    }
    lastContent() {
        const elements = R.slice(0, this.sequence, this.elements(0));
        if (elements.length === 0) {
            return "";
        }
        return R.last(elements).name;
    }
    render() {
        const elements = this.elements(this.sequence);
        return html ` ${this.transition
            ? html `<img
          src="${paths.chatbotTransitionGif}"
          class="absolute"
          style="${styleMap({ top: "-170px", "z-index": "3" })}"
        />`
            : html ` <div class="chat-bot-content-scroll-area">
            ${R.map((seq) => seq.html, elements)}
          </div>
          ${this.lastContent() === "response" ? this.createResponse() : nothing}`}`;
    }
    updated() {
        const scrollArea = this.querySelector(".chat-bot-content-scroll-area");
        // 다음 프레잉에 렌더가 된 다음에 스크롤한다.
        setTimeout(() => {
            // scrollArea는 transition상태에서 널일 수 있다.
            if (scrollArea != null) {
                scrollArea.scrollTop = scrollArea.scrollHeight;
            }
        }, 0);
    }
    elements(showCount) {
        const showContext = {
            showCount,
        };
        const mapIndexed = R.addIndex(R.map);
        const natashaSpeaking1 = () => mapIndexed((text, index) => seq(`natasha-${index}`, html `<jh-chat-bot-content-text-element
              text=${text}
              class=${classMap({
            dn: this.showOrNotClass(showContext) === "hide",
        })}
            >
            </jh-chat-bot-content-text-element>`), R.slice(0, 2, texts.natasha));
        const natashaSpeaking2 = () => mapIndexed((text, index) => seq(`natasha-${index + 2}`, html `<jh-chat-bot-content-text-element
              text=${text}
              class=${classMap({
            dn: this.showOrNotClass(showContext) === "hide",
        })}
            ></jh-chat-bot-content-text-element>`), R.slice(2, 7, texts.natasha));
        const natashaSpeaking3 = () => mapIndexed((text, index) => seq(`natasha-${index + 7}`, html `<jh-chat-bot-content-text-element
              text=${text}
              class=${classMap({
            dn: this.showOrNotClass(showContext) === "hide",
        })}
            ></jh-chat-bot-content-text-element>`), R.slice(7, 8, texts.natasha));
        return [
            seq("entrance", html `<jh-chat-bot-content-entrance
          text="${texts.entrance}"
          class=${classMap({
                dn: this.showOrNotClass(showContext) === "hide",
            })}
        ></jh-chat-bot-content-entrance>`),
            ...natashaSpeaking1(),
            seq("natashaImage", html `<jh-chat-bot-content-natasha-image
          class=${classMap({
                dn: this.showOrNotClass(showContext) === "hide",
            })}
        ></jh-chat-bot-content-natasha-image>`),
            ...natashaSpeaking2(),
            seq("response", (() => {
                if (showContext.showCount > 0) {
                    showContext.showCount -= 1;
                }
                return nothing;
            })()),
            seq("responseText", html `<jh-chat-bot-content-response-text
          text=${texts.response}
          class=${classMap({
                dn: this.showOrNotClass(showContext) === "hide",
            })}
        ></jh-chat-bot-content-response-text>`),
            ...natashaSpeaking3(),
            seq("printing", html `<jh-chat-bot-content-printing
          class=${classMap({
                dn: this.showOrNotClass(showContext) === "hide",
            })}
        ></jh-chat-bot-content-printing>`),
            seq("finished", html `<jh-chat-bot-content-text-element
          text="${texts.natasha[8]}"
          class=${classMap({
                dn: this.showOrNotClass(showContext) === "hide",
            })}
        ></jh-chat-bot-content-text-element>`),
        ];
    }
    showOrNotClass(context) {
        if (context.showCount === 0) {
            return "hide";
        }
        else {
            context.showCount -= 1;
            return "show";
        }
    }
    createResponse() {
        return html `<jh-chat-bot-content-response
      @click="${this.handleResponseClick}"
    ></jh-chat-bot-content-response>`;
    }
    handleResponseClick(ev) {
        ev.stopPropagation();
        this.dispatchEvent(new CustomEvent("responseClick"));
    }
}
customElements.define("jh-chat-bot-content", ChatBotContent);
export class ChatBotContentController {
    constructor(chatBot) {
        const contentElement = chatBot.find("jh-chat-bot-content");
        if (contentElement.length === 0) {
            console.error(`Can't find content ${contentElement}`);
            console.log({
                contentElement,
            });
        }
        this.content = contentElement;
        this.rawContent = this.content[0];
    }
    /**
     * 챗봇 텍스트 재생하는 함수
     */
    async startTimer(sound) {
        const { canceler, promise: blinkPromise } = this.blinkText();
        let ticketPrintSoundCanceler = null;
        let sequence = 0;
        while (true) {
            this.content.attr("sequence", String(sequence));
            sequence += 1;
            const lastContentName = this.rawContent.lastContent();
            if (lastContentName === "entrance" || lastContentName === "response") {
                sound.play("chatbotEffect1");
                console.log("entrance");
            }
            else if (lastContentName === "natashaImage") {
                sound.play("chatbotNatashaAppear");
            }
            else if (lastContentName === "responseText") {
                sound.play("chatbotEffect3");
            }
            else if (lastContentName === "printing") {
                // do nothing
            }
            else if (lastContentName.length > 0) {
                console.log({ lastContentName });
                sound.play("chatbotEffect2");
            }
            await delayFor(lastContentName);
            if (lastContentName === "response") {
                const onEvent = new OnEvent({
                    eventName: "responseClick",
                    selector: this.rawContent,
                });
                await onEvent.waitOnce();
            }
            else if (lastContentName === "printing") {
                // wait for printing element is created
                await delay(0);
                ticketPrintSoundCanceler = playTicketPrintSound(sound);
                await loadAnnounce(this.rawContent.querySelector("jh-chat-bot-content-printing"));
                sound.playLoop("chatbotTicketEffect2");
            }
            else if (lastContentName === "finished") {
                break;
            }
        }
        const ticket = this.rawContent.querySelector("jh-chat-bot-content-printing");
        const onEvent = new OnEvent({
            eventName: "clickTicket",
            selector: ticket,
        });
        await onEvent.waitOnce();
        assertNotNull(ticketPrintSoundCanceler, "ticketPrintSoundCanceler");
        ticketPrintSoundCanceler.cancel();
        sound.stopLoop("chatbotTicketEffect2");
        sound.play("chatbotTicketTouchEffect");
        canceler.cancel();
        // this.content.attr("transition", "");
        await endingEffect({ root: $("jh-chat-bot")[0] });
        // await delay(1000);
        // blinkPromise는 await한 순간 끝나야해.
        await blinkPromise;
        sound.stopLoop("chatbotBgm");
    }
    blinkText() {
        const loop = async (canceler) => {
            while (true) {
                await delay(500);
                canceler.check();
                this.content.toggleClass("no-red");
            }
        };
        return cancel.root(loop);
    }
}
/**
 * 티켓을 프린팅 하는 동안 안내 방송 다운로드
 */
async function loadAnnounce(printing) {
    /**
     * 여러곳에서 같은 변수를 수정하기 위해서 오브젝트를 만듬
     */
    const context = {
        prevRawProgress: 0,
        rawProgress: 0,
        prevProgress: 0,
    };
    const startTimeMillis = Date.now();
    // ms
    const minimumTookTime = getSkip().getSkippableTimeInMS(10 * 1000);
    const loading = load({
        onProgress: (progress) => {
            context.prevRawProgress = context.rawProgress;
            context.rawProgress = progress * 100;
        },
        onComplete: () => null,
        filePaths: announcePaths,
    });
    while (true) {
        // 30 fps
        await delay(33);
        const tookTime = Date.now() - startTimeMillis;
        const maximumProgress = (tookTime / minimumTookTime) * 100;
        const minimumProgress = Math.min(context.prevProgress + 0.01, 90);
        let progress = Math.max(minimumProgress, context.rawProgress);
        progress = Math.min(maximumProgress, progress);
        printing.setAttribute("progress", String(progress));
        context.prevProgress = progress;
        if (progress === 100) {
            break;
        }
    }
    await loading;
}
async function delayFor(lastContentName) {
    const multiplier = 1;
    if (lastContentName.length === 0) {
        return;
    }
    if (R.includes(lastContentName, [
        "printing",
        "response",
        "responseText",
        "finished",
    ])) {
        const time = 1000 * multiplier;
        await delay(time);
    }
    else if (R.includes(lastContentName, [
        "entrance",
        "natasha-0",
        "natasha-1",
        "natasha-6",
        "natasha-7",
    ])) {
        const time = 1200 * multiplier;
        await delay(time);
    }
    else {
        const time = 3000 * multiplier;
        await delay(time);
    }
}
//# sourceMappingURL=chatBotContent.js.map